import { PlusOutlined } from '@ant-design/icons'
import { getFileType } from '@entities/post/model/helpers/getFileType'
import type { FileDTO } from '@entities/post/model/types'
import { MessageType } from '@entities/post/model/types'
import { useJWTTokensStore } from '@shared/auth'
import type { GetProp, UploadFile, UploadProps } from 'antd'
import { Image, Modal, theme, Upload } from 'antd'
import type { UploadChangeParam } from 'antd/lib/upload'
import { useState } from 'react'

const { Dragger } = Upload

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = (error) => reject(error)
    })

export const CustomUpload = (props: UploadProps<FileDTO>) => {
    const { onChange, fileList } = props
    const {
        token: { colorTextLabel, colorText }
    } = theme.useToken()
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewImage, setPreviewImage] = useState<string>()
    const [previewVideoUrl, setPreviewVideoUrl] = useState<string>()
    const [isSaving, setIsSaving] = useState(false)

    const isAudio = fileList
        ? fileList[0]?.type === MessageType.AUDIO ||
          fileList[0]?.response?.file_type === MessageType.AUDIO ||
          fileList[0]?.type === MessageType.VOICE ||
          fileList[0]?.response?.file_type === MessageType.VOICE
        : false

    const isDocument = fileList
        ? fileList[0]?.type === MessageType.DOCUMENT || fileList[0]?.response?.file_type === MessageType.DOCUMENT
        : false

    const handlePreview = async (file: UploadFile<FileDTO>) => {
        const type = getFileType(file) !== 'none' ? getFileType(file) : file?.type
        switch (type) {
            case 'photo':
                if (!file.url && !file.preview && file?.originFileObj) {
                    file.preview = await getBase64(file.originFileObj as FileType)
                }
                setPreviewImage(file.url || (file.preview as string))
                setPreviewOpen(true)
                break
            case 'video':
                setPreviewVideoUrl(file.response?.file_url)
                setPreviewOpen(true)
        }
    }

    const access = useJWTTokensStore.getState().access

    const handleChange = (info: UploadChangeParam<UploadFile<FileDTO>>) => {
        const { status, percent } = info.file

        if (status === 'done') {
            switch (getFileType(info.file)) {
                case 'video':
                    info.file.thumbUrl = info.file.response?.preview_url
            }
        } else if (status === 'uploading' && percent === 100) {
            setIsSaving(true)
        } else {
            setIsSaving(false)
        }

        onChange?.(info)
    }
    const isDragger = props.multiple || (fileList && fileList?.length > 1)
    const ComponentUpload = isDragger ? Dragger : Upload
    return (
        <>
            <ComponentUpload
                headers={{
                    Authorization: `Bearer ${access}`
                }}
                data={(file) => ({
                    file_type: getFileType(file)
                })}
                {...props}
                locale={{
                    uploading: isSaving ? 'Сохраняем' : 'Загружаем'
                }}
                onPreview={handlePreview}
                showUploadList={{
                    showPreviewIcon: !isAudio && !isDocument
                }}
                onChange={handleChange}
                style={{ marginBottom: isDragger ? 10 : 0 }}
            >
                <button style={{ border: 0, background: 'none' }} type="button">
                    <PlusOutlined style={{ color: colorTextLabel }} />
                    <div style={{ marginTop: 8, color: colorTextLabel }}>
                        {isDragger ? 'Нажмите или перетащите файлы' : 'Загрузить'}
                    </div>
                </button>
            </ComponentUpload>
            {previewImage && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) => !visible && setPreviewImage('')
                    }}
                    src={previewImage}
                />
            )}
            {previewVideoUrl && (
                <Modal open={previewOpen} onCancel={() => setPreviewOpen(false)} footer={null} centered destroyOnClose>
                    <video style={{ width: '100%' }} controls>
                        <source src={previewVideoUrl} />
                        Your browser does not support the video tag. Ваш браузер не поддерживает аудио-плеер.
                    </video>
                </Modal>
            )}
            {isAudio && props.fileList && (
                <>
                    {props.fileList.map((item) => (
                        <>
                            <span style={{ margin: '10px 0 5px 0', display: 'inline-block' }}>{item.name}</span>
                            <audio style={{ width: '100%' }} controls>
                                <source src={item?.response?.file_url} />
                                Your browser does not support the video tag. Ваш браузер не поддерживает аудио-плеер.
                            </audio>
                        </>
                    ))}
                </>
            )}
        </>
    )
}
